import {
    createContext,
    useEffect,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";

import i18n from "i18n";
import {
    applyDocumentLang,
    loadStoredLocale,
    persistLocale,
    type AppLocale,
} from "i18n/locale";
import { momentLocaleForApp } from "i18n/index";

async function loadMomentLocale(momentCode: string) {
    switch (momentCode) {
        case "zh-tw":
            // @ts-ignore moment ships locale JavaScript without declarations
            await import("moment/locale/zh-tw");
            break;
        case "it":
            // @ts-ignore moment ships locale JavaScript without declarations
            await import("moment/locale/it");
            break;
        case "ja":
            // @ts-ignore moment ships locale JavaScript without declarations
            await import("moment/locale/ja");
            break;
        default:
            break;
    }
    return import("moment");
}

type LocalePreferencesValue = {
    locale: AppLocale;
    setLocale: (locale: AppLocale) => void;
};

const LocalePreferencesContext = createContext<LocalePreferencesValue | null>(null);
LocalePreferencesContext.displayName = "LocalePreferences";

export const useLocalePreferences = (): LocalePreferencesValue => {
    const ctx = useContext(LocalePreferencesContext);
    if (!ctx) {
        throw new Error(
            "useLocalePreferences must be used within LocalePreferencesProvider"
        );
    }
    return ctx;
};

export const LocalePreferencesProvider = ({ children }: { children: ReactNode }) => {
    const [locale, setLocaleState] = useState<AppLocale>(loadStoredLocale);

    useEffect(() => {
        const momentCode = momentLocaleForApp(locale);
        void loadMomentLocale(momentCode).then(({ default: moment }) => {
            moment.locale(momentCode);
        });
    }, [locale]);

    const setLocale = useCallback(
        (next: AppLocale) => {
            if (next === locale) return;
            setLocaleState(next);
            persistLocale(next);
            applyDocumentLang(next);
            const momentCode = momentLocaleForApp(next);
            void loadMomentLocale(momentCode).then(({ default: moment }) => {
                moment.locale(momentCode);
            });
            void i18n.changeLanguage(next);
        },
        [locale],
    );

    const value = useMemo(
        () => ({
            locale,
            setLocale,
        }),
        [locale, setLocale],
    );

    return (
        <LocalePreferencesContext.Provider value={value}>
            {children}
        </LocalePreferencesContext.Provider>
    );
};
